/* ─── THE L3 ADD-ON DECLARATION ────────────────────────────────────────────────────────────────
   #148, from #139's resolution. One stored value holds the reader's answer to "do I need the L3?",
   and this module is the only thing in the theme that spells its key.

   THE THEME'S FIRST JAVASCRIPT ASSET, AND WHY IT IS ONE

   Every other script in this theme lives in its section's or its snippet's own script block,
   which Shopify bundles per file. Three sections need this same fifteen lines —
   `academy-hero-single` here, plus `academy-hero-carousel` and `academy-tabs`/`academy-card`
   (#149, #150) — and a section's block cannot be shared with another section. Three copies is
   three chances for one of them to spell `cohr:l3-addon` differently, and the reader's answer
   would then survive on one surface and not the next. Same argument `snippets/order-field.liquid`
   won on (`CONVENTIONS.md` §33, "one snippet, not four copies").

   🚨 IT OWNS NO ARITHMETIC AND NO MONEY FORMATTING.

   Both fees are already in the DOM, rendered twice by `snippets/academy-price.liquid` and
   formatted by `| money` — see `sections/academy-hero-single.liquid`'s fee card. This moves the
   `hidden` attribute between them and nothing else. No currency symbol, no separator and no
   decimal place is decided here (#94, #96).

   🚨 THE POLARITY. Ticked = "I need the L3", and the fee goes UP. Unticked — the reader already
   holds it — is the served default and the honest lead. Ticking is never a discount and draws no
   strike; the early bird is the only discount on this site.

   THE STORE IS `localStorage`, AND THAT IS AN INSTRUCTION, NOT A DEFAULT

   Matt, 1 September 2026, on #139's prototype. The `sessionStorage` argument was heard and
   rejected there. A future session that wants the answer to expire raises expiry as its own
   question — it does not quietly change the store underneath this.

   `'1'` and `'0'`, because the value is read by `=== '1'` and an absent key, an unreadable store
   and a `'0'` all have to mean the same thing: unticked, which is what the server rendered.
   Every access is wrapped: Safari in private browsing and a browser with site data blocked both
   throw on `localStorage`, and a page that cannot remember the answer must still show it.

   THE MARKUP CONTRACT — TWO ATTRIBUTES, AND BOTH ARE OPTIONAL

     [data-cohr-l3]        a shelf holding exactly one checkbox. Seeded from the store on load,
                           and writes to it on change. A disabled checkbox is neither (#274). A surface may render none — `academy-card`
                           is one grid cell wide and deliberately carries no control (#150).
     [data-cohr-l3-fees]   a scope holding one [data-l3="held"] and, WHERE THAT ACADEMY RUNS
                           the add-on, one [data-l3="needed"] beside it. A scope with only the
                           first half is ordinary — every past academy and every academy
                           without `cohr.l3_addon` renders one — and `swap` leaves it alone.
                           The two halves are usually a pair of fees, and since #150 they are also
                           a listing's pair of notes — one sentence for each reader, in one slot.
                           This module moves `hidden` and never looks at what is inside.
     [data-cohr-l3-declare] a scope whose links are a declaration: "1" the reader needs the L3,
                           "0" they hold it. A click on a link or a button inside it writes the
                           store and applies it, before the navigation the link is about to do.
                           No checkbox anywhere — this is a reader answering by choosing a route.

   🚨 A DECLARING SCOPE MUST BE WORDED AS ONE, AND THE HOME HERO IS THE CASE IT WAS BUILT FOR

   `sections/home-hero-carousel.liquid`'s two panels are "Already qualified?" and "Never trained
   before?" — the reader picks one and then presses *Book an academy*. #139: *"Would be a terrible
   experience to click 'Book this academy' with the L3 add-on switched on, and then land on a
   product page with the toggle unset."* Since #150 that landing page is every listing too, so the
   declaration has to be made at the click or the cards are wrong for the reader who just told the
   page who they are.

   **Both panels declare, not only panel 2.** Panel 1 writing `'0'` is what makes the mirror case
   right: someone who ticked the shelf in March, comes back, presses *Already qualified?* and must
   not land on the higher fee. A panel that declares in one direction only can correct a reader and
   never uncorrect them. It follows that a scope may only be put on markup whose words are a
   self-selection — the panel headings are, and the theme editor's own group labels
   ("Panel 1 — already qualified", "Panel 2 — new to it") fix that, which is why the value is read
   from the panel index rather than from a setting nobody could word.

   🚨 A `[data-l3="needed"]` block ships `hidden`, and `[hidden]` needs an explicit
   `display: none` wherever the block's own rule sets a `display` — an author declaration beats
   the UA sheet's and the two fees draw on top of each other (#97's trap). That rule belongs to
   the section that renders the pair, not here.

   IT RUNS BEFORE THE SECTION BUNDLE, BY DOCUMENT ORDER

   `layout/theme.liquid` loads this `defer` **above** `content_for_header`, which is where Shopify
   injects `sections-script` and `snippets-script` — also `defer`, and deferred scripts execute in
   document order. So every switch on the page already agrees with the store by the time
   `academy-hero-single`'s own startup `sync()` reads one. That section keeps its swap: it is the
   change-driven half for its own fee pair, this is the stored half for every pair on the page,
   and both read the same one truth, so they cannot disagree.

   A SECOND TAB IS THE SAME READER

   `storage` fires in the other tabs, never the one that wrote, so the listener re-applies without
   any risk of a loop. Note that seeding a checkbox in code fires no `change` event — which is why
   this module swaps the fees itself rather than leaving it to the section's listener. */
(function () {
  const KEY = 'cohr:l3-addon';
  const wired = new WeakSet();

  function read() {
    try {
      return window.localStorage.getItem(KEY) === '1';
    } catch (error) {
      return false;
    }
  }

  function write(declared) {
    try {
      window.localStorage.setItem(KEY, declared ? '1' : '0');
    } catch (error) {
      /* Nothing to report and nothing to retry. The page is still correct for this visit; it
         simply will not remember the answer for the next one. */
    }
  }

  /* Every switch on the page says what the store says. Set directly, so no `change` fires and
     nothing writes back — this is the store talking to the page, not the reader.

     🚨 EXCEPT A DISABLED ONE (#274). Past an academy's Movement Foundations cut-off its switch is
     served disabled and unticked, and it stays unticked: unticked is what books the held fee, the
     only one that academy still sells. The store is not touched — it is shared across academies,
     and the reader's "I need it" must still be there on the next open one. Liquid also drops that
     academy's `needed` fee block, so `swap` below finds one half and leaves the held fee alone. */
  function seed(declared) {
    document.querySelectorAll('[data-cohr-l3] input[type="checkbox"]').forEach(function (input) {
      if (input.disabled) return;
      input.checked = declared;
    });
  }

  /* Every rendered pair of fees shows the one the reader asked for. */
  function swap(declared) {
    document.querySelectorAll('[data-cohr-l3-fees]').forEach(function (scope) {
      const held = scope.querySelector('[data-l3="held"]');
      const needed = scope.querySelector('[data-l3="needed"]');
      /* 🚨 A SCOPE WITH ONLY ONE HALF IS ORDINARY, NOT IMPOSSIBLE — do not delete this
         guard as dead. `academy-card` puts the scope on the card's foot, which exists on
         every card, and renders the second block only where that academy actually runs the
         add-on: every past academy and every academy without `cohr.l3_addon` takes this
         branch, on every listing. Returning early leaves that card's one honest fee on
         screen. Hiding `held` with nothing to show in its place would blank the price, and
         a swap against a null would throw — taking the rest of the page's switches with it. */
      if (!held || !needed) return;
      held.hidden = declared;
      needed.hidden = !declared;
    });
  }

  function apply(declared) {
    seed(declared);
    swap(declared);
  }

  /* A route out of a panel that describes the reader is the reader saying which one they are.
     Delegated, so it is one listener per scope and it cannot miss a link added later; the write is
     synchronous, so it has landed by the time the browser follows the href. */
  function wireDeclarations() {
    document.querySelectorAll('[data-cohr-l3-declare]').forEach(function (scope) {
      if (wired.has(scope)) return;
      wired.add(scope);
      scope.addEventListener('click', function (event) {
        /* The scope holds the panel's buttons; a click on the padding between them declares
           nothing. `closest` also catches the click landing on a label or an icon inside one. */
        if (!event.target.closest('a, button')) return;
        const declared = scope.getAttribute('data-cohr-l3-declare') === '1';
        write(declared);
        /* The reader is usually navigating away, so this is for the case they are not: an in-page
           link, or a listing already on the screen below the panel they pressed. */
        apply(declared);
      });
    });
  }

  function wire() {
    document.querySelectorAll('[data-cohr-l3] input[type="checkbox"]').forEach(function (input) {
      if (wired.has(input)) return;
      wired.add(input);
      input.addEventListener('change', function () {
        write(input.checked);
        /* Not just this switch: a page may carry several (every panel of the carousel does), and
           the one the reader pressed is not allowed to be the only one that agrees. */
        apply(input.checked);
      });
    });
    wireDeclarations();
    apply(read());
  }

  /* `defer` means the document is parsed by the time this runs, but the guard costs one line and
     keeps the module correct if it is ever loaded another way. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }

  /* The theme editor swaps a section's markup without reloading, which would otherwise leave the
     new switch unseeded and the new fee pair showing the served default. */
  document.addEventListener('shopify:section:load', wire);

  window.addEventListener('storage', function (event) {
    if (event.key === KEY) apply(read());
  });
})();
